import { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/** Varumärkessuffix i webbläsarfliken: "<Sida> - PartyFabriken". */
export const SITE_NAME = 'PartyFabriken';

/**
 * Ruta → fliktitel-nyckel. Matchas i ordning (första träff vinner), så lägg
 * mer specifika mönster före mindre specifika. Mönstren följer App.js-rutterna;
 * exakt matchning (react-router `matchPath`, end=true som default) gör att
 * t.ex. "/leagues" inte fångar "/leagues/me". Nycklarna översätts i
 * DocumentTitle (källtexten ligger i locales under `titles.*`).
 */
const TITLE_ROUTES = [
  { pattern: '/', titleKey: 'titles.home' },

  // Statistik
  { pattern: '/stats/leaderboard', titleKey: 'titles.leaderboard' },
  { pattern: '/stats/maps', titleKey: 'titles.maps' },
  { pattern: '/stats/medaljer', titleKey: 'titles.medals' },
  { pattern: '/stats/season', titleKey: 'titles.season' },
  { pattern: '/stats/players/:steamid64', titleKey: 'titles.playerProfile' },
  { pattern: '/stats/players', titleKey: 'titles.players' },
  { pattern: '/stats/matches/:matchid', titleKey: 'titles.match' },
  { pattern: '/stats/matches', titleKey: 'titles.matches' },
  { pattern: '/stats/h2h/:steamA/:steamB', titleKey: 'titles.h2h' },
  { pattern: '/highlights', titleKey: 'titles.highlights' },
  { pattern: '/suggest-map', titleKey: 'titles.suggestMap' },

  // Turneringar & nyheter
  { pattern: '/tournaments/:id', titleKey: 'titles.tournament' },
  { pattern: '/tournaments', titleKey: 'titles.tournaments' },
  { pattern: '/news/:slug', titleKey: 'titles.newsPost' },
  { pattern: '/news', titleKey: 'titles.news' },

  // Ligor
  { pattern: '/leagues/teams/new', titleKey: 'titles.createTeam' },
  { pattern: '/leagues/teams/:teamId', titleKey: 'titles.team' },
  { pattern: '/leagues/teams', titleKey: 'titles.team' },
  { pattern: '/leagues/me', titleKey: 'titles.myLeagues' },
  { pattern: '/leagues/:id', titleKey: 'titles.league' },
  { pattern: '/leagues', titleKey: 'titles.leagues' },

  // Match-flöde (hybrid)
  { pattern: '/queue', titleKey: 'titles.queue' },
  { pattern: '/create', titleKey: 'titles.createMatch' },
  { pattern: '/lobby/:matchid', titleKey: 'titles.lobby' },
  { pattern: '/veto/:matchid', titleKey: 'titles.veto' },
  { pattern: '/profile', titleKey: 'titles.profile' },
  { pattern: '/webadmin/*', titleKey: 'titles.admin' },
  { pattern: '/live', titleKey: 'titles.live' },

  // Overwatch
  { pattern: '/overwatch/admin', titleKey: 'titles.overwatchAdmin' },
  { pattern: '/overwatch/report/:reportId', titleKey: 'titles.overwatchReport' },
  { pattern: '/overwatch/player/:steamId', titleKey: 'titles.overwatchPlayer' },
  { pattern: '/overwatch', titleKey: 'titles.overwatch' },

  // Auth
  { pattern: '/login', titleKey: 'titles.login' },
  { pattern: '/pending', titleKey: 'titles.pending' },
  { pattern: '/auth/callback', titleKey: 'titles.authCallback' },
];

/**
 * Returnerar översättningsnyckeln för en pathname, eller null om ingen ruta
 * matchar. Översätts av anroparen via `t()`.
 */
export function titleKeyForPath(pathname) {
  for (const route of TITLE_ROUTES) {
    if (matchPath(route.pattern, pathname)) return route.titleKey;
  }
  return null;
}

/**
 * Renderlös komponent som håller `document.title` i synk med aktuell ruta.
 * Monteras en gång inuti <BrowserRouter>. Sätter "<Sida> - PartyFabriken",
 * eller bara "PartyFabriken" för okända rutter. Titeln uppdateras även när
 * användaren byter språk.
 */
export default function DocumentTitle() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const key = titleKeyForPath(pathname);
    document.title = key ? `${t(key)} - ${SITE_NAME}` : SITE_NAME;
  }, [pathname, t, i18n.resolvedLanguage]);

  return null;
}
